import { useState } from 'react';

import { api, type Trunk } from './api.js';

interface Props {
  trunk: Trunk | null;
  onDone: () => void;
  onCancel: () => void;
}

/**
 * Trunk credential form.
 *
 * The one screen that has to be right — everything about connecting to a
 * carrier is entered here, and nothing is ever written to a config file by
 * hand. Saving regenerates the gateway XML and reloads sofia.
 */
export function TrunkForm({ trunk, onDone, onCancel }: Props): JSX.Element {
  const [form, setForm] = useState({
    name: trunk?.name ?? '',
    enabled: trunk?.enabled ?? false,
    sipHost: trunk?.sipHost ?? '',
    sipPort: trunk?.sipPort ?? 5060,
    transport: trunk?.transport ?? 'tls',
    outboundProxy: trunk?.outboundProxy ?? '',
    authMode: trunk?.authMode ?? 'register',
    username: trunk?.username ?? '',
    password: '',
    authRealm: trunk?.authRealm ?? '',
    fromDomain: trunk?.fromDomain ?? '',
    callerIdInFrom: trunk?.callerIdInFrom ?? true,
    callerIdNumber: trunk?.callerIdNumber ?? '',
    codecPrefs: trunk?.codecPrefs ?? 'OPUS,PCMA,PCMU',
    maxChannels: trunk?.maxChannels ?? 15,
    dialPrefix: trunk?.dialPrefix ?? '',
    stripDigits: trunk?.stripDigits ?? 0,
    priority: trunk?.priority ?? 100,
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // An empty password on edit means "leave the stored one alone" — sending
    // it would wipe working credentials every time someone tweaks a port.
    const body: Record<string, unknown> = {
      ...form,
      outboundProxy: form.outboundProxy || null,
      authRealm: form.authRealm || null,
      fromDomain: form.fromDomain || null,
      callerIdNumber: form.callerIdNumber || null,
      sipPort: Number(form.sipPort),
      maxChannels: Number(form.maxChannels),
      stripDigits: Number(form.stripDigits),
      priority: Number(form.priority),
    };
    if (!form.password) delete body.password;

    try {
      if (trunk) await api.updateTrunk(trunk.id, body);
      else await api.createTrunk(body);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <form className="card form" onSubmit={(e) => void submit(e)}>
      <h2>{trunk ? `Edit ${trunk.name}` : 'Add trunk'}</h2>

      <div className="grid-2">
        <label>
          <span>Name</span>
          <input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="my-carrier"
            required
            disabled={!!trunk}
          />
          <small>Letters, numbers, hyphen, underscore. Used as the gateway name.</small>
        </label>

        <label>
          <span>Priority</span>
          <input
            type="number"
            value={form.priority}
            onChange={(e) => set('priority', Number(e.target.value))}
          />
          <small>Lower is tried first. Add a second trunk for failover.</small>
        </label>
      </div>

      <h3>Connection</h3>
      <div className="grid-3">
        <label>
          <span>SIP host</span>
          <input
            value={form.sipHost}
            onChange={(e) => set('sipHost', e.target.value)}
            placeholder="sip.carrier.com"
            required
          />
        </label>

        <label>
          <span>Port</span>
          <input
            type="number"
            value={form.sipPort}
            onChange={(e) => set('sipPort', Number(e.target.value))}
          />
        </label>

        <label>
          <span>Transport</span>
          <select
            value={form.transport}
            onChange={(e) => set('transport', e.target.value as typeof form.transport)}
          >
            <option value="tls">TLS (recommended)</option>
            <option value="tcp">TCP</option>
            <option value="udp">UDP</option>
          </select>
        </label>
      </div>

      <label>
        <span>Outbound proxy (optional)</span>
        <input
          value={form.outboundProxy}
          onChange={(e) => set('outboundProxy', e.target.value)}
          placeholder="Leave blank unless your carrier requires one"
        />
      </label>

      <h3>Authentication</h3>
      <label>
        <span>Mode</span>
        <select
          value={form.authMode}
          onChange={(e) => set('authMode', e.target.value as typeof form.authMode)}
        >
          <option value="register">Register (username + password)</option>
          <option value="ip">IP authentication (carrier allowlists your IP)</option>
        </select>
      </label>

      {form.authMode === 'register' && (
        <div className="grid-2">
          <label>
            <span>Username</span>
            <input
              value={form.username}
              onChange={(e) => set('username', e.target.value)}
              autoComplete="off"
              required
            />
          </label>

          <label>
            <span>Password</span>
            <input
              type="password"
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              autoComplete="new-password"
              placeholder={trunk?.hasPassword ? 'Unchanged' : ''}
              required={!trunk}
            />
            <small>
              {trunk?.hasPassword
                ? 'Leave blank to keep the stored password.'
                : 'Encrypted at rest; never shown again.'}
            </small>
          </label>
        </div>
      )}

      <div className="grid-2">
        <label>
          <span>Auth realm (optional)</span>
          <input
            value={form.authRealm}
            onChange={(e) => set('authRealm', e.target.value)}
            placeholder="Defaults to SIP host"
          />
        </label>

        <label>
          <span>From domain (optional)</span>
          <input
            value={form.fromDomain}
            onChange={(e) => set('fromDomain', e.target.value)}
            placeholder="Defaults to auth realm"
          />
        </label>
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.callerIdInFrom}
          onChange={(e) => set('callerIdInFrom', e.target.checked)}
        />
        <span>Put caller ID number in the From header</span>
      </label>
      <small>
        On for most carriers. Turn <strong>off</strong> for carriers that require
        the registered SIP username in From instead (sipgate rejects otherwise:
        &ldquo;403 Username in From Field required&rdquo;).
      </small>

      <h3>Dialling</h3>
      <div className="grid-3">
        <label>
          <span>Strip digits</span>
          <input
            type="number"
            min={0}
            value={form.stripDigits}
            onChange={(e) => set('stripDigits', Number(e.target.value))}
          />
          <small>Removed from the front of the number.</small>
        </label>

        <label>
          <span>Dial prefix</span>
          <input
            value={form.dialPrefix}
            onChange={(e) => set('dialPrefix', e.target.value)}
          />
          <small>Prepended after stripping. E.g. 00 or +.</small>
        </label>

        <label>
          <span>Max channels</span>
          <input
            type="number"
            min={1}
            value={form.maxChannels}
            onChange={(e) => set('maxChannels', Number(e.target.value))}
          />
          <small>Hard concurrency cap. Limits toll-fraud exposure.</small>
        </label>
      </div>

      <label>
        <span>Codecs</span>
        <input
          value={form.codecPrefs}
          onChange={(e) => set('codecPrefs', e.target.value)}
        />
        <small>In preference order. Most carriers want PCMA or PCMU.</small>
      </label>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => set('enabled', e.target.checked)}
        />
        <span>Enabled</span>
      </label>

      {form.enabled && (
        <p className="warn">
          Inbound calls also require this carrier's signalling IPs in the
          <code> trunks </code> ACL, then a FreeSWITCH restart.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button type="button" className="secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" disabled={busy}>
          {busy ? 'Saving…' : trunk ? 'Save changes' : 'Add trunk'}
        </button>
      </div>
    </form>
  );
}
